import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import MoveItemsModal from '../../../../components/move-items-modal'
import { useSnackbar } from '../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../components/stories/molecules/snackbar/constants'
import { useMoveProjectsAndFoldersMutation } from '../../../../react-query/project/mutation/use-move-projects-and-folders-mutation'
import {
  projectMenuApiErrorCodeOptions,
  projectMenuItemTypeOptions
} from '../../constants'
import { useCheckChildrenNameIsAvailableMutation } from '../../../../react-query/project/mutation/use-check-children-name-is-available-mutation'
import { isEmpty } from 'lodash'
import BlockMoveMultipleItemsMessage from '../../../../components/move-items-modal/block-move-multiple-items-message'
import PropTypes from 'prop-types'

const CompanyMoveMultipleItemsModal = ({
  onClose = () => {},
  onMoved = () => {},
  selectedFolders = [],
  selectedProjects = []
}) => {
  const { formatMessage } = useIntl()
  const { addSnackbar } = useSnackbar()
  const [moveItemsError, setMoveItemsError] = useState('')

  const selected = useMemo(
    () => ({
      folders: {
        ids: selectedFolders.map((f) => f.id),
        names: selectedFolders.map((f) => f.name)
      },
      projects: {
        ids: selectedProjects.map((p) => p.id),
        names: selectedProjects.map((p) => p.name)
      }
    }),
    [selectedFolders, selectedProjects]
  )

  const totalSelectedItems =
    selected.folders.ids.length + selected.projects.ids.length

  const [destinationFolderHint, setDestinationFolderHint] = useState(null)

  // handlers
  const getItemType = (name) =>
    selected.folders.names.includes(name)
      ? projectMenuItemTypeOptions.FOLDER
      : projectMenuItemTypeOptions.PROJECT

  const showSnackbarSuccess = () => {
    addSnackbar({
      message: formatMessage({ id: 'moveItems_items_moved' }),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  // handle callbacks
  const handleMoveItemsSuccess = () => {
    if (isEmpty(moveItemsError)) {
      onClose()
      onMoved()
      showSnackbarSuccess()
    }
  }

  const handleMoveItemsError = (error) => {
    // user doesn't have permission to move items
    if (
      error &&
      error?.response?.data?.status ===
        projectMenuApiErrorCodeOptions.NO_PERMISSION_TO_CREATE_PROJECT_OR_FOLDER
    ) {
      return setMoveItemsError(
        formatMessage({
          id: 'projectMenu_no_permission_to_move'
        })
      )
    }

    // folder with the same name already exists in the destination folder
    if (
      error &&
      error?.response?.data?.status ===
        projectMenuApiErrorCodeOptions.FOLDER_NAME_IS_NOT_AVAILABLE
    ) {
      return setDestinationFolderHint(
        formatMessage({
          id: 'projectMenu_folder_with_this_name_aready_exists'
        })
      )
    }

    if (
      error &&
      error?.response?.data?.status ===
        projectMenuApiErrorCodeOptions.FOLDER_NO_LOGER_EXISTS
    ) {
      return setDestinationFolderHint(
        formatMessage({
          id: 'recursiveFolderList_the_selected_folder_no_longer_exists'
        })
      )
    }

    return setMoveItemsError(
      formatMessage({
        id: 'moveItems_move_error'
      })
    )
  }

  const handleCheckMoveItemsNameSuccess = (data, variables) => {
    const { projectNames: checkedItemNames } = data
    const { destinationFolderId } = variables

    // split the object into 2 arrays - allowed and blocked
    const allowedNames = Object.entries(checkedItemNames)
      .filter(([_, item]) => item.allowed)
      .map(([name]) => name)

    const blockedItems = Object.entries(checkedItemNames)
      .filter(([_, item]) => !item.allowed)
      .map(([name]) => {
        const itemType = getItemType(name)

        return { name, itemType }
      })

    // show message with blocked items

    if (!isEmpty(blockedItems)) {
      setMoveItemsError(
        <BlockMoveMultipleItemsMessage
          blockedItems={blockedItems}
          allowedItems={allowedNames}
        />
      )
    }

    // move only allowed items
    const allowedFolderIds = selectedFolders
      .filter((folder) => allowedNames.includes(folder.name))
      .map((folder) => folder.id)

    const allowedProjectIds = selectedProjects
      .filter((project) => allowedNames.includes(project.name))
      .map((project) => project.id)

    if (isEmpty(allowedProjectIds) && isEmpty(allowedFolderIds)) return null

    moveItems({
      projectIds: allowedProjectIds,
      folderIds: allowedFolderIds,
      destinationFolderId
    })
  }

  const handleCheckMoveItemsNameError = () => {
    return setMoveItemsError(
      formatMessage({
        id: 'moveItems_move_error'
      })
    )
  }

  // mutations

  const { mutate: moveItems, isPending: moveItemsLoading } =
    useMoveProjectsAndFoldersMutation({
      onSuccess: handleMoveItemsSuccess,
      onError: handleMoveItemsError
    })

  const { mutate: checkMoveItems, isPending: checkMoveItemsLoading } =
    useCheckChildrenNameIsAvailableMutation({
      onSuccess: handleCheckMoveItemsNameSuccess,
      onError: handleCheckMoveItemsNameError
    })

  const handleMoveItems = ({ parentFolderId = null }) => {
    setMoveItemsError(null)
    setDestinationFolderHint(null)

    const selectedItemNames = [
      ...selected.folders.names,
      ...selected.projects.names
    ]

    checkMoveItems({
      selectedItemNames,
      destinationFolderId: parentFolderId
    })
  }

  const isLoading = moveItemsLoading || checkMoveItemsLoading

  if (totalSelectedItems === 0) return null

  return (
    <MoveItemsModal
      handleClose={onClose}
      handleMove={handleMoveItems}
      isLoading={false}
      onMoveLoading={isLoading}
      onMoveError={moveItemsError}
      destinationFolderHint={destinationFolderHint}
      modalTitle={formatMessage({ id: 'move' })}
      isMultipleItems={true}
      totalSelectedItems={totalSelectedItems}
    />
  )
}

CompanyMoveMultipleItemsModal.propTypes = {
  onClose: PropTypes.func,
  onMoved: PropTypes.func,
  selectedFolders: PropTypes.array,
  selectedProjects: PropTypes.array
}

export default CompanyMoveMultipleItemsModal
